<template>
  <ValidationObserver ref="form">
    <ValidationProvider v-slot="{ errors }" name="name" rules="required">
      <v-text-field
        v-model="interviewFormatData.interviewName"
        label="Name"
        :error-messages="getErrorMessages(errors)"
      />
    </ValidationProvider>
    <ValidationProvider
      v-slot="{ errors }"
      name="duration"
      rules="required|minNumber:0"
    >
      <v-text-field
        :error-messages="getErrorMessages(errors)"
        v-model="interviewFormatData.duration"
        type="number"
        label="Duration (minutes)"
      />
    </ValidationProvider>
    <div class="mb-2">Candidate Details</div>
    <quill-editor
      v-model="interviewFormatData.candidateDetails"
      :options="editorOptions"
      class="mb-5"
    />
    <div>
      <div class="detail-title">Files for candidate</div>
      <div class="mt-4">
        <FileUpload
          class="mt-2"
          @unselectFile="data => unselectFile(data, false)"
          @selectFile="data => selectFile(data, false)"
          :files="interviewFormatData.externalFiles"
          :fileTypes="['application/pdf']"
          disableAutoProceed
          showPreview
          customActions
          isMultiple
          @show="showFile"
        />
        <v-skeleton-loader v-if="isFetchingExternalFiles" type="list-item" />
      </div>
    </div>
    <div class="mb-2">Interviewer Details</div>
    <quill-editor
      v-model="interviewFormatData.interviewerDetails"
      :options="editorOptions"
      class="mb-5"
    />
    <div>
      <div class="detail-title">Files for interviewer</div>
      <div class="mt-4">
        <FileUpload
          class="mt-2"
          @unselectFile="data => unselectFile(data, true)"
          @selectFile="data => selectFile(data, true)"
          :files="interviewFormatData.internalFiles"
          :fileTypes="['application/pdf']"
          disableAutoProceed
          showPreview
          customActions
          isMultiple
          @show="showFile"
        />
        <v-skeleton-loader v-if="isFetchingInternalFiles" type="list-item" />
      </div>
    </div>
    <PDFViewFull :data="fileDialog" />
  </ValidationObserver>
</template>

<script>
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { FETCH_INTERVIEW_FORMAT_FILES } from "@/store/modules/bookings/interviews/actions";
import { createNamespacedHelpers } from "vuex";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import PDFViewFull from "@/components/common/PDFViewFull";
import FileUpload from "@/components/common/FileUpload";
import { remove, forEach, map } from "lodash";
import APIService from "@/services/APIService";
import { getFilePreviewSettings } from "@/utils/files";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { REMOVE_FILE } from "@/store/modules/data/actions";
import { DATA_NAMESPACE } from "@/store/modules/data";

const { mapActions: mapInterviewActions } = createNamespacedHelpers(
  INTERVIEWS_NAMESPACE
);

const { mapActions: mapDataActions } = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  name: "InterviewFormatForm",
  props: {
    interviewFormat: Object
  },
  components: {
    PDFViewFull,
    FileUpload,
    ValidationProvider,
    ValidationObserver
  },
  created() {
    if (this.interviewFormat) {
      this.interviewFormatData = {
        ...this.interviewFormatData,
        ...this.interviewFormat
      };
      this.fetchFiles();
    }
  },
  data() {
    return {
      isLoading: false,
      interviewFormatData: {
        interviewerDetails: "",
        candidateDetails: "",
        internalFiles: [],
        externalFiles: []
      },
      editorOptions: {
        modules: {
          toolbar: {
            container: DEFAULT_TOOLBAR_OPTIONS
          }
        }
      },
      showValidationErrors: false,
      internalUppyInstance: null,
      externalUppyInstance: null,
      fileDialog: { dialog: false },
      isFetchingInternalFiles: false,
      isFetchingExternalFiles: false,
      filesToBeRemoved: []
    };
  },
  computed: {
    interviewFormatId() {
      return this.interviewFormat && this.interviewFormat.id;
    }
  },
  methods: {
    ...mapInterviewActions({
      fetchInterviewFormatFiles: FETCH_INTERVIEW_FORMAT_FILES
    }),
    ...mapDataActions({
      removeFile: REMOVE_FILE
    }),
    fetchFiles() {
      this.fetchInternalFiles();
      this.fetchExternalFiles();
    },
    async fetchInternalFiles() {
      this.isFetchingInternalFiles = true;
      this.interviewFormatData.internalFiles = await this.fetchInterviewFormatFiles(
        {
          interviewFormatId: this.interviewFormatId,
          collection: "internal"
        }
      );
      this.isFetchingInternalFiles = false;
    },
    async isFormValid() {
      this.showValidationErrors = true;
      return this.$refs.form.validate();
    },
    async fetchExternalFiles() {
      this.isFetchingExternalFiles = true;
      this.interviewFormatData.externalFiles = await this.fetchInterviewFormatFiles(
        {
          interviewFormatId: this.interviewFormatId,
          collection: "external"
        }
      );
      this.isFetchingExternalFiles = false;
    },
    async uploadFiles(interviewId) {
      await this.uploadInternalFiles(interviewId);
      await this.uploadExternalFiles(interviewId);
    },
    async removeFiles() {
      const requests = map(this.filesToBeRemoved, ({ url }) =>
        this.removeFile(url)
      );
      return Promise.all(requests);
    },
    async uploadInternalFiles(interviewId) {
      await Promise.all(
        forEach(this.interviewFormatData.internalFiles, ({ id }) =>
          this.setFileUrl(interviewId, id, true)
        )
      );
      this.internalUppyInstance &&
        (await this.internalUppyInstance.upload(interviewId));
    },
    async uploadExternalFiles(interviewId) {
      await Promise.all(
        forEach(this.interviewFormatData.externalFiles, ({ id }) =>
          this.setFileUrl(interviewId, id, false)
        )
      );
      this.externalUppyInstance &&
        (await this.externalUppyInstance.upload(interviewId));
    },
    async setFileUrl(interviewId, fileId, isInternal) {
      if (isInternal && this.internalUppyInstance) {
        this.internalUppyInstance.setFileState(fileId, {
          xhrUpload: {
            endpoint: this.getFileUrl(interviewId, isInternal)
          }
        });
      } else if (this.externalUppyInstance) {
        this.externalUppyInstance.setFileState(fileId, {
          xhrUpload: {
            endpoint: this.getFileUrl(interviewId, isInternal)
          }
        });
      }
    },
    getFileUrl(interviewId, isInternal) {
      const host = APIService.host;
      const collection = isInternal ? "internal" : "external";
      return `${host}/interview-formats/${interviewId}/file/${collection}`;
    },
    selectFile({ uppyInstance, file }, isInternal) {
      if (isInternal) {
        this.internalUppyInstance = uppyInstance;
        this.interviewFormatData.internalFiles.push(file);
        return;
      }
      this.externalUppyInstance = uppyInstance;
      this.interviewFormatData.externalFiles.push(file);
    },
    unselectFile({ uppyInstance, file: fileToBeRemoved }, isInternal) {
      if (fileToBeRemoved.url) {
        this.filesToBeRemoved.push(fileToBeRemoved);
      }
      isInternal
        ? remove(
            this.interviewFormatData.internalFiles,
            file => file.id === fileToBeRemoved.id
          )
        : remove(
            this.interviewFormatData.externalFiles,
            file => file.id === fileToBeRemoved.id
          );
      uppyInstance.reset();
    },
    async showFile(file) {
      const filePreviewsettings = await getFilePreviewSettings(file);
      this.fileDialog = {
        ...filePreviewsettings,
        dialog: true
      };
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    }
  }
};
</script>

<style></style>
